import { Fragment, HTMLProps, useEffect, useRef, useState } from "react";

import date from "date-and-time";

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";


import { IQuote } from "../../models/Quote";
import classNames from "../../utils/classNames";
import Pagination from "../shared/Tables/Pagination";
import openInNewTab from "../../utils/openInNewTab";
import LeadCustomer from "../leads/LeadTable/LeadCustomer";
import QuoteOwnerPill from "../shared/Quotes/QuoteOwnerPill";
import QuoteWebsitePill from "../shared/Quotes/QuoteWebsitePill";
import PagedTable from "../shared/Tables/PagedTable";

const colHelper = createColumnHelper<IQuote>();

const cols = [
    colHelper.accessor("step", {
        header: "Step",
        enableSorting: true,
    }),
    colHelper.accessor("furthestStep", {
        header: "Futhest",
        enableSorting: true,
    }),
    colHelper.accessor("vehicleRegistration", {
        header: "VRM",
        enableSorting: false,
    }),
    colHelper.accessor("postcode", {
        header: "Postcode",
        enableSorting: false,
    }),
    colHelper.accessor("vehicleMake", {
        header: "Make",
        enableSorting: false,
    }),
    colHelper.accessor("vehicleModel", {
        header: "Model",
        enableSorting: false,
    }),
    colHelper.display({
        id: "collection",
        header: "Collection",
        sortingFn: "auto",
        meta: { sortKey: "priceCollected" },
        cell: (info) => (
          <span className="flex" >
            <span className="ml-4 m-auto">
                {info.row.original.priceCollected != -1
                ? `£${info.row.original.priceCollected.toFixed(2)}`
                : `-`
                }
            </span>
          </span>
        )
    }),
    colHelper.display({
        id: "dropOff",
        header: "Drop Off",
        sortingFn: "auto",
        meta: { sortKey: "priceDropOff" },
        cell: (info) => (
          <span className="flex" >
            <span className="ml-4 m-auto">
                {info.row.original.priceDropOff != -1
                ? `£${info.row.original.priceDropOff.toFixed(2)}`
                : `-`
                }
            </span>
          </span>
        )
    }),
    colHelper.accessor("contact.fullname", {
        header: "Name",
        enableSorting: false,
    }),
    colHelper.accessor("contact.telephone1", {
        header: "Phone",
        enableSorting: false,
    }),
    colHelper.accessor("contact.email", {
        header: "Email",
        enableSorting: false,
    }),
    colHelper.display({
        id: "source",
        header: "Source",
        // Derived from several source ids; no single column to order by server-side.
        enableSorting: false,
        cell: (info) => (
          <QuoteWebsitePill
            sourceCustomerId={info.row.original.sourceCustomerId ?? null}
            sourceWebsiteId={info.row.original.sourceWebsiteId ?? null}
            websiteDomain={info.row.original.sourceWebsiteDomain ?? null}
            websitePrimaryColor={info.row.original.sourceWebsitePrimaryColor ?? null}
            showLabel={false}
            fallbackLabel="Widget"
          />
        )
    }),
    colHelper.accessor("createdDate", {
        header: "Created Date",
        sortingFn: "auto",
        cell: (created) => (
          <span>
            {date.format(new Date(created.getValue()!), "DD/MM/YYYY")}
          </span>
        ),
    }),
];

const savCols = [
  ...cols,
  colHelper.display({
    id: "customer",
    header: "Customer",
    // Falls back across three customer id fields; no single column to order by server-side.
    enableSorting: false,
    cell: (info) => (
        <QuoteOwnerPill 
            customerId={
                info.row.original.sourceCustomerId 
                ?? info.row.original.collectedCustomerId
                ?? info.row.original.dropOffCustomerId
                ?? null
            } 
            showLabel={false}
        />
    ),
  }),
]

export default function QuotesTable({ 
    url, 
    isSav,
    customUrlQuery,
    externalQuery,
    queryKey,
}: { url: string, isSav: boolean, customUrlQuery?: string, externalQuery?: string, queryKey?: string }) {
    const [sorting, setSorting] = useState<SortingState>([]);


    const handleClassNames = (quote: IQuote) => {
        let classNames = "";

        let lookup = {
            0: "!bg-gray-100/50 hover:!bg-gray-200/80",
            1: "!bg-gray-100/50 hover:!bg-gray-200/80",
            2: "!bg-gray-100/50 hover:!bg-gray-200/80",
            3: "!bg-gray-100/50 hover:!bg-gray-200/80",
            4: "!bg-gray-100/50 hover:!bg-gray-200/80",
            5: "!bg-orange-200/50 hover:!bg-orange-200/80",
            6: "!bg-yellow-200/50 hover:!bg-yellow-200/80",
            7: "!bg-green-200/50 hover:!bg-green-200/80",
            8: "!bg-purple-200/50 hover:!bg-purple-200/80",
        }
    
        classNames = lookup[quote.step as keyof typeof lookup] //"bg-green-200/80"  

        return classNames;
    };

    return (
        <>
            <div className="bg-white shadow rounded-lg mt-4 p-4">
                <PagedTable<IQuote>
                    key={url}
                    url={url}
                    columns={isSav ? savCols : cols}
                    showTotal={true}
                    initialSort={[{ id: "createdDate", desc: true }]}
                    // cellPadding="px-[0px] py-[0px]"
                    fontSize="text-xs"
                    containerClassName="max-h-[56vh] overflow-y-scroll"
                    condensedView={true}
                    rowColor={handleClassNames}
                    onRowClick={(quote: IQuote) => openInNewTab(`abandoned-quotes/${quote.id}`)}
                    customUrlQuery={customUrlQuery}
                    externalQuery={externalQuery}
                    queryKey={queryKey}
                />
            </div>
        </>
    )
}